import { useCallback, useState } from "react";

/** Two objects that hold the same values compare equal whatever the order their keys were built in. */
function snapshotOf(value: unknown): string {
  return JSON.stringify(value, (_key, item) =>
    item !== null && typeof item === "object" && !Array.isArray(item)
      ? Object.fromEntries(Object.entries(item).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)))
      : item,
  );
}

/**
 * Does the form hold something the server does not? Compares the values the form is showing now with
 * the values it was OPENED with (or last saved with) — a field changed and put back is not a change,
 * and a form that has only just loaded is not one either.
 *
 *   values  what the operator can edit, in a shape that says the same thing when it says the same
 *           thing: sort a list whose order means nothing, leave out a client-only key (a React key),
 *           turn a `Map` into an object. Never the server's copy: a refetch that changes it must not
 *           make an untouched form dirty.
 *   ready   the form holds its opening values. A form that is filled in from data that arrives later
 *           is not ready until the render where that data is in its fields (the same batch that sets
 *           its "primed" flag): the values of that render are the clean ones. A form that is opened
 *           empty is ready from the first render. When it stops being ready (the address changed and
 *           the form is being loaded again) the clean values are forgotten and taken again.
 *
 * `reset()` says "these values, as of the next render, are the saved ones": call it in the same
 * batch as the `setState` that puts the saved values into the fields (after a save that keeps the
 * operator on the page, after the operator asked to reload the server's copy).
 */
export function useDirtyValues(values: unknown, ready = true): { dirty: boolean; reset: () => void } {
  const snapshot = snapshotOf(values);
  const [clean, setClean] = useState<string | null>(null);
  // Adjusting state during render (React re-renders at once, before anything is committed): the
  // clean values are the ones of the FIRST render where the form is ready — read there, not in an
  // effect, so that no render ever shows a loaded form as different from itself.
  if (ready && clean === null) setClean(snapshot);
  else if (!ready && clean !== null) setClean(null);
  const reset = useCallback(() => setClean(null), []);
  return { dirty: ready && clean !== null && snapshot !== clean, reset };
}
